import { z } from 'zod';

export const REPORT_CHROME_CONTRACT_VERSION = 4;
export const REPORT_CHROME_LEGACY_CONTRACT_VERSION = 3;
export const REPORT_CHROME_PARTS = Object.freeze(['header', 'footer']);

const sha256 = z.string().regex(/^[a-f0-9]{64}$/);
const uuid = z.string().uuid();
const operationId = z.string().trim().min(1).max(120).regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,119}$/);
const utcDateTime = z.string().datetime({ offset: true }).refine((value) => value.endsWith('Z'), {
    message: 'Date must be an ISO UTC timestamp',
});
const contextText = (max) => z.string().max(max);

export const REPORT_CHROME_PARAMETERS = Object.freeze([
    Object.freeze({ key: 'DocumentNo', description: Object.freeze({ tr: 'Belge numarası', en: 'Document number' }) }),
    Object.freeze({ key: 'DocumentName', description: Object.freeze({ tr: 'Belge adı', en: 'Document name' }) }),
    Object.freeze({ key: 'RevisionNo', description: Object.freeze({ tr: 'Revizyon numarası', en: 'Revision number' }) }),
    Object.freeze({ key: 'RevisionDate', description: Object.freeze({ tr: 'Revizyon tarihi', en: 'Revision date' }) }),
    Object.freeze({ key: 'Classification', description: Object.freeze({ tr: 'Gizlilik sınıfı', en: 'Classification' }) }),
    Object.freeze({ key: 'ReportTitle', description: Object.freeze({ tr: 'Rapor başlığı', en: 'Report title' }) }),
    Object.freeze({ key: 'Subtitle', description: Object.freeze({ tr: 'Kısa açıklama', en: 'Subtitle' }) }),
    Object.freeze({ key: 'GeneratedAt', description: Object.freeze({ tr: 'Oluşturulma zamanı', en: 'Generated at' }) }),
    Object.freeze({ key: 'PreparedBy', description: Object.freeze({ tr: 'Hazırlayan', en: 'Prepared by' }) }),
]);

export const REPORT_CHROME_PARAMETER_KEYS = Object.freeze(
    REPORT_CHROME_PARAMETERS.map(({ key }) => key),
);

export const ReportChromeLayoutRefSchema = z.object({
    sha256,
    contractVersion: z.literal(REPORT_CHROME_CONTRACT_VERSION),
    updatedAt: utcDateTime,
}).strict();

export const ReportChromeHistoricalLayoutRefSchema = z.object({
    sha256,
    contractVersion: z.union([
        z.literal(REPORT_CHROME_LEGACY_CONTRACT_VERSION),
        z.literal(REPORT_CHROME_CONTRACT_VERSION),
    ]),
    updatedAt: utcDateTime,
}).strict();

export const ReportPresentationOptionsSchema = z.object({
    title: z.string().trim().max(200).optional(),
    subtitle: z.string().trim().max(500).optional(),
    showScopeSummary: z.boolean().optional(),
}).strict();

export const ReportPresentationSchema = z.object({
    title: z.string().trim().min(1).max(200),
    subtitle: z.string().trim().max(500),
    showScopeSummary: z.boolean(),
}).strict();

export const ReportChromeDocumentContextSchema = z.object({
    kind: z.enum(['report', 'document']),
    documentNo: contextText(200),
    documentName: contextText(500),
    revisionNo: contextText(120),
    revisionDate: contextText(80),
    classification: z.string().trim().min(1).max(160),
    classificationColor: z.string().trim().regex(/^#[0-9a-f]{6}$/i),
    accentColor: z.string().trim().regex(/^#[0-9a-f]{6}$/i),
    reportTitle: z.string().trim().min(1).max(500),
    generatedAt: utcDateTime,
    preparedBy: contextText(200),
}).strict();
export const ReportChromeDocumentContextRequestSchema = z.object({
    documentId: z.string().regex(/^[0-9a-fA-F]{24}$/),
}).strict();

export const ReportChromeReportContextRequestSchema = z.object({
    reportTitle: z.string().trim().min(1).max(500),
    generatedAtUtc: z.coerce.date(),
}).strict();

export const ReportChromeSaveReceiptParamsSchema = z.object({
    editSessionId: uuid,
    saveId: uuid,
}).strict();



export const ReportChromeSaveReceiptSchema = z.object({
    editSessionId: uuid,
    saveId: uuid,
    sha256,
    contractVersion: z.literal(REPORT_CHROME_CONTRACT_VERSION),
}).strict();

export const ReportChromeCommitSchema = z.object({
    editSessionId: uuid,
    saveId: uuid,
    sha256,
    expectedVersion: z.number().int().nonnegative(),
    operationId,
}).strict();

export const ReportChromeResetSchema = z.object({
    expectedVersion: z.number().int().nonnegative(),
    operationId,
}).strict();

export const ReportChromeDesignerCapabilitySchema = z.object({
    part: z.enum(REPORT_CHROME_PARTS),
    editSessionId: uuid.optional(),
}).strict();
export const ReportChromePreviewSchema = z.object({
    mode: z.enum(['normal', 'document']),
    locale: z.enum(['tr', 'en']).default('tr'),
    layoutBase64: z.string().min(1).max(8_000_000),
}).strict();
